/**
 * Grouping speaker embeddings into speakers.
 *
 * ADR 0001 measured the key fact driving this module: the embedding models
 * separate voices with a wide margin, but picking a distance threshold to cut
 * at is fragile — the correct answer sits in a narrow band that moves with
 * quantization. When the number of people is known, cutting the dendrogram at
 * exactly K is reliable where thresholding is not. Hence resolveSpeakerCount.
 */

/** Distance in [0, 2]: 0 identical direction, 1 orthogonal, 2 opposite. */
export function cosineDistance(a: Float32Array, b: Float32Array): number {
  let dot = 0;
  for (let i = 0; i < a.length; i++) dot += a[i]! * b[i]!;
  return 1 - dot;
}

/** Unit-normalises a vector so cosine distance is a plain dot product. */
export function normalise(v: Float32Array): Float32Array {
  let sum = 0;
  for (let i = 0; i < v.length; i++) sum += v[i]! * v[i]!;
  const n = Math.sqrt(sum);
  if (n === 0) return v;
  const out = new Float32Array(v.length);
  for (let i = 0; i < v.length; i++) out[i] = v[i]! / n;
  return out;
}

/**
 * Cosine distance threshold used when the speaker count is unknown.
 * ADR 0001 swept this: the correct count appears between 0.7 and 0.8 across
 * test recordings, with 0.7 the safer end (it splits rather than merges, and a
 * split is recoverable by the user while a merge silently loses a person).
 */
export const DEFAULT_THRESHOLD = 0.7;

export interface ClusterOptions {
  /** Cut at exactly this many clusters. Takes precedence over threshold. */
  k?: number;
  /** Stop merging once the closest pair exceeds this distance. */
  threshold?: number;
}

/**
 * Average-linkage agglomerative clustering. Returns a label per input vector.
 *
 * Pairwise distances are computed once, and after each merge the distance from
 * the new group to every other is the size-weighted mean of the two it came
 * from (the Lance–Williams update for average linkage), which is exactly the
 * mean of the member-to-member distances without recomputing them. That
 * makes a merge O(n) and the whole run O(n²) in memory and O(n³) in scalar
 * operations, with no cosine products inside the loop.
 *
 * Sized for conversation: the live path re-clusters every sample heard so far
 * after each 10-second block, an hour is a few hundred samples, and a phone
 * has one slow core. 300 samples cluster in well under a second.
 */
export function agglomerative(vectors: readonly Float32Array[], opts: ClusterOptions): number[] {
  const n = vectors.length;
  if (n === 0) return [];
  if (n === 1) return [0];

  // Distance between groups, indexed by the group's original position. A
  // merged group keeps the lower index; the higher one goes inactive.
  const dist = new Float32Array(n * n);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = cosineDistance(vectors[i]!, vectors[j]!);
      dist[i * n + j] = d;
      dist[j * n + i] = d;
    }
  }
  const active = new Uint8Array(n).fill(1);
  const size = new Int32Array(n).fill(1);
  const members: number[][] = vectors.map((_, i) => [i]);
  let remaining = n;

  const targetK = opts.k !== undefined ? Math.max(1, Math.min(opts.k, n)) : 1;
  const threshold = opts.threshold;

  for (;;) {
    if (opts.k !== undefined && remaining <= targetK) break;
    if (remaining === 1) break;

    let best = Infinity;
    let bi = -1;
    let bj = -1;
    for (let i = 0; i < n; i++) {
      if (!active[i]) continue;
      for (let j = i + 1; j < n; j++) {
        if (!active[j]) continue;
        const d = dist[i * n + j]!;
        if (d < best) { best = d; bi = i; bj = j; }
      }
    }
    if (bi < 0) break;
    if (opts.k === undefined && threshold !== undefined && best > threshold) break;

    const si = size[bi]!;
    const sj = size[bj]!;
    for (let k = 0; k < n; k++) {
      if (!active[k] || k === bi || k === bj) continue;
      const d = (si * dist[bi * n + k]! + sj * dist[bj * n + k]!) / (si + sj);
      dist[bi * n + k] = d;
      dist[k * n + bi] = d;
    }
    members[bi] = members[bi]!.concat(members[bj]!);
    size[bi] = si + sj;
    active[bj] = 0;
    remaining--;
  }

  // Label by first appearance so output is stable and readable.
  const groups = members.filter((_, i) => active[i]).sort((a, b) => Math.min(...a) - Math.min(...b));
  const labels = new Array<number>(n);
  groups.forEach((g, k) => g.forEach((i) => { labels[i] = k; }));
  return labels;
}

/**
 * Below this many segments the mean is dominated by whoever happens to be in
 * it, so subtracting it would remove speaker identity rather than channel.
 */
const MIN_FOR_CENTRING = 3;

/**
 * Removes the component every embedding shares, which is the recording channel.
 *
 * A phone codec, a band-limited microphone or a room all colour every segment
 * of a recording the same way. That shared colouring appears as a large common
 * component in every embedding, and because it is identical everywhere it
 * dominates the cosine distances and buries the differences between people. The
 * symptom is unmistakable: one speaker is credited with nearly the whole
 * conversation.
 *
 * Measured on a band-limited copy of a two-speaker recording: 91%/9% before
 * centring, 58%/42% after, with the clean original unchanged at 55%/45%.
 *
 * Note that this widens distances overall, so a threshold calibrated on
 * uncentred embeddings no longer applies — see how diarize.ts uses this.
 */
export function centreEmbeddings(vectors: readonly Float32Array[]): Float32Array[] {
  if (vectors.length < MIN_FOR_CENTRING) return [...vectors];
  const dim = vectors[0]!.length;
  const mean = new Float32Array(dim);
  for (const v of vectors) for (let i = 0; i < dim; i++) mean[i] = mean[i]! + v[i]! / vectors.length;
  return vectors.map((v) => normalise(Float32Array.from(v, (x, i) => x - mean[i]!)));
}

export interface AbsorbOptions {
  minSegments: number;
  minDurationMs: number;
}

/**
 * Folds clusters too small to be a real participant into their nearest
 * neighbour. A cluster survives if it has enough segments OR enough total
 * speech: someone who said one long thing counts, and so does someone who
 * chipped in briefly many times.
 */
export function absorbTinyClusters(
  vectors: readonly Float32Array[],
  labels: readonly number[],
  durationsMs: readonly number[],
  opts: AbsorbOptions,
): number[] {
  const members = new Map<number, number[]>();
  labels.forEach((l, i) => {
    const arr = members.get(l);
    if (arr) arr.push(i); else members.set(l, [i]);
  });
  if (members.size <= 1) return [...labels];

  const totalMs = (idxs: readonly number[]) => idxs.reduce((s, i) => s + (durationsMs[i] ?? 0), 0);
  const centroid = (idxs: readonly number[]): Float32Array => {
    const dim = vectors[idxs[0]!]!.length;
    const c = new Float32Array(dim);
    for (const i of idxs) for (let d = 0; d < dim; d++) c[d] = c[d]! + vectors[i]![d]!;
    return normalise(c);
  };

  const healthy = [...members.entries()].filter(
    ([, idxs]) => idxs.length >= opts.minSegments || totalMs(idxs) >= opts.minDurationMs);
  const tiny = [...members.entries()].filter(
    ([, idxs]) => !(idxs.length >= opts.minSegments || totalMs(idxs) >= opts.minDurationMs));

  // Nothing to absorb into: leave the partition untouched rather than collapse it.
  if (healthy.length === 0 || tiny.length === 0) return [...labels];

  const healthyCentroids = healthy.map(([label, idxs]) => ({ label, c: centroid(idxs) }));
  const out = [...labels];
  for (const [, idxs] of tiny) {
    const c = centroid(idxs);
    let bestLabel = healthyCentroids[0]!.label;
    let bestDist = Infinity;
    for (const h of healthyCentroids) {
      const d = cosineDistance(c, h.c);
      if (d < bestDist) { bestDist = d; bestLabel = h.label; }
    }
    for (const i of idxs) out[i] = bestLabel;
  }
  return out;
}

/**
 * Extra clusters allowed when the room is known to contain voices that are not
 * participants — a television, the next table.
 *
 * This is off by default, and that default is measured. Against human
 * annotation, headroom makes things worse when there are no intruders: a
 * two-woman conversation went from 62/38 to 72/28 against a true 60/40, and a
 * four-person meeting's error rate rose from 0.199 to 0.219. Surplus groups are
 * usually not intruders — they are one person who sounded different for a while
 * — and discarding them throws away real speech.
 *
 * When there really is a television, the picture reverses. Cutting at exactly
 * the number of people forces those voices into somebody's tally, and to free a
 * slot it merges two real people into one. On a recording with chatter under a
 * four-person meeting, headroom moved the split from 31/31/20/18 to 35/23/22/20
 * against a true 34/23/22/21.
 *
 * Nothing in the audio reliably distinguishes the two situations — surplus
 * groups in the two-woman case sat further from the main groups (1.181) than
 * genuine intruders did (0.865). The person in the room knows; Ronda does not.
 * So it is asked, not guessed.
 */
export const CLUSTER_HEADROOM = 2;

export const OTHER_VOICE = -1;

/**
 * Keeps the `k` clusters with the most speech and marks the rest as other
 * voices. Labels are renumbered 0..k-1 by how much each speaks.
 */
export function keepBusiest(
  labels: readonly number[],
  durationsMs: readonly number[],
  k: number,
): number[] {
  if (k <= 0) return labels.map(() => OTHER_VOICE);

  const totals = new Map<number, number>();
  labels.forEach((l, i) => totals.set(l, (totals.get(l) ?? 0) + (durationsMs[i] ?? 0)));
  if (totals.size <= k) {
    // Nothing to discard, but still renumber by prominence.
    const order = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([l]) => l);
    const rank = new Map(order.map((l, i) => [l, i]));
    return labels.map((l) => rank.get(l) ?? OTHER_VOICE);
  }

  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]);
  const rank = new Map(ranked.slice(0, k).map(([l], i) => [l, i]));
  return labels.map((l) => rank.get(l) ?? OTHER_VOICE);
}

/**
 * Extra groups to cut before keeping the busiest `k`, when the head count is
 * known and there is enough evidence for the extra groups to mean anything.
 *
 * Over a long conversation on a table microphone, a person's voice drifts:
 * they turn away, lean back, get animated. A few of their segments end up far
 * from the rest, and average linkage, cutting at exactly k, isolates those
 * outliers as a group of their own. To free the slot it merges two real people
 * — on a 17-minute four-person meeting, the two men. Cutting wider lets the
 * outliers form splinter groups that are then placed back onto the k main
 * groups (see placeSplinters) instead of costing a person.
 *
 * The headroom grows with the number of voice samples because with few
 * samples the busiest groups are not yet the real people: on the opening
 * minutes of that meeting, where one person presents and the others barely
 * speak, a fixed headroom of eight discarded a real participant's only group.
 * One extra group per 25 samples, capped at eight, leaves short sessions
 * exactly as they were and gives long ones the room they need. Measured in
 * ADR 0005.
 */
export function splinterHeadroom(samples: number): number {
  return Math.min(8, Math.floor(samples / 25));
}

/**
 * Gives every segment marked OTHER_VOICE to the nearest kept group, by
 * distance to that group's duration-weighted centroid. Kept labels are left
 * alone. With nothing kept there is nothing to place into, and the labels are
 * returned unchanged.
 */
export function placeSplinters(
  vectors: readonly Float32Array[],
  labels: readonly number[],
  durationsMs: readonly number[],
): number[] {
  const groups = new Map<number, number[]>();
  labels.forEach((l, i) => {
    if (l === OTHER_VOICE) return;
    const g = groups.get(l);
    if (g) g.push(i); else groups.set(l, [i]);
  });
  if (groups.size === 0 || !labels.includes(OTHER_VOICE)) return [...labels];

  const centroids = [...groups.entries()].map(([label, idxs]) => {
    const c = new Float32Array(vectors[idxs[0]!]!.length);
    for (const i of idxs) {
      const w = durationsMs[i] ?? 0;
      for (let d = 0; d < c.length; d++) c[d] = c[d]! + vectors[i]![d]! * w;
    }
    return { label, c: normalise(c) };
  });

  return labels.map((l, i) => {
    if (l !== OTHER_VOICE) return l;
    let best = centroids[0]!.label;
    let bestDist = Infinity;
    for (const { label, c } of centroids) {
      const d = cosineDistance(vectors[i]!, c);
      if (d < bestDist) { bestDist = d; best = label; }
    }
    return best;
  });
}

/**
 * Carries speaker identities across a re-clustering.
 *
 * The live path clusters every sample heard so far from scratch after each
 * window, and the groups come back numbered by how much each has spoken.
 * Those numbers are not identities: when one person overtakes another, they
 * swap. This matches each new group with the previous identity it shares the
 * most speech with — most of a person's samples stay together from one
 * clustering to the next — so the person who was "1" stays "1", whatever
 * their rank. A group that matches nothing takes an identity that fell vacant
 * in this round, and only when none did does a fresh one appear; samples
 * marked OTHER_VOICE stay unattributed.
 *
 * `previous` holds the identity of each sample after the last clustering and
 * may be shorter than `labels` when new samples arrived. Identities are
 * numbered from `nextId` upwards, in order of each new group's first sample.
 */
export function carryIdentities(
  labels: readonly number[],
  previous: readonly number[],
  durationsMs: readonly number[],
  nextId: number,
): { identities: number[]; nextId: number } {
  // Shared speech between each new group and each previous identity.
  const shared = new Map<number, Map<number, number>>();
  const firstIndex = new Map<number, number>();
  labels.forEach((l, i) => {
    if (l === OTHER_VOICE) return;
    if (!firstIndex.has(l)) firstIndex.set(l, i);
    const prev = previous[i];
    if (prev === undefined || prev === OTHER_VOICE) return;
    const row = shared.get(l) ?? new Map<number, number>();
    row.set(prev, (row.get(prev) ?? 0) + (durationsMs[i] ?? 0));
    shared.set(l, row);
  });

  // Greedy: the strongest overlaps claim their identities first.
  const pairs: { label: number; identity: number; ms: number }[] = [];
  for (const [label, row] of shared) for (const [identity, ms] of row) pairs.push({ label, identity, ms });
  pairs.sort((a, b) => b.ms - a.ms || a.label - b.label);
  const identityOf = new Map<number, number>();
  const taken = new Set<number>();
  for (const { label, identity, ms } of pairs) {
    if (ms <= 0 || identityOf.has(label) || taken.has(identity)) continue;
    identityOf.set(label, identity);
    taken.add(identity);
  }

  // A group that matched nothing takes an identity that fell vacant — the
  // people at the table have not changed, one of them has simply been
  // re-sorted — and only when none is vacant does a new identity appear. So
  // with the head count known, the identities never outnumber the people.
  const unmatched = [...firstIndex.entries()].sort((a, b) => a[1] - b[1]).map(([label]) => label).filter((l) => !identityOf.has(l));
  const vacant = [...new Set(previous.filter((p) => p !== OTHER_VOICE && p !== undefined))].filter((p) => !taken.has(p)).sort((a, b) => a - b);
  let next = nextId;
  for (const label of unmatched) {
    const reuse = vacant.shift();
    identityOf.set(label, reuse ?? next++);
  }

  return {
    identities: labels.map((l) => (l === OTHER_VOICE ? OTHER_VOICE : identityOf.get(l)!)),
    nextId: next,
  };
}

/**
 * Above this spread, the voices are arriving too muddled for moment-to-moment
 * attribution to be trusted. On the annotated table recordings each person's
 * samples sit 0.19–0.55 from their own centroid (median); the one speaker
 * measured at 0.79 was the one whose short samples landed on other people,
 * and a field session at 0.68 and 0.79 had an unreliable "now" indicator.
 */
export const CLARITY_LIMIT = 0.6;
/** Fewer samples than this for a person and their spread means nothing yet. */
const MIN_FOR_CLARITY = 3;

/**
 * How clearly the voices arrive: for each person with enough samples, the
 * median cosine distance of their samples to their own centroid, in centred
 * space; the spread reported is the worst person's. Distance to the phone,
 * reverberation, street noise and a phone's own audio processing all raise
 * it, and all of them are things the people at the table can change.
 */
export function assessClarity(
  centred: readonly Float32Array[],
  identities: readonly number[],
  durationsMs: readonly number[],
): { spread: number; clear: boolean } {
  const groups = new Map<number, number[]>();
  identities.forEach((id, i) => {
    if (id === OTHER_VOICE) return;
    const g = groups.get(id);
    if (g) g.push(i); else groups.set(id, [i]);
  });
  let spread = 0;
  for (const idxs of groups.values()) {
    if (idxs.length < MIN_FOR_CLARITY) continue;
    const c = new Float32Array(centred[0]!.length);
    for (const i of idxs) for (let d = 0; d < c.length; d++) c[d] = c[d]! + centred[i]![d]! * (durationsMs[i] ?? 0);
    const centre = normalise(c);
    const ds = idxs.map((i) => cosineDistance(centred[i]!, centre)).sort((a, b) => a - b);
    spread = Math.max(spread, ds[Math.floor(ds.length / 2)]!);
  }
  return { spread, clear: spread <= CLARITY_LIMIT };
}

export type SpeakerCountSource = 'calibration' | 'count' | 'names' | 'automatic';

export interface SpeakerCountHint {
  /** Null means "work it out from the audio". */
  k: number | null;
  source: SpeakerCountSource;
  confident: boolean;
}

/**
 * The tiered strategy: use the most reliable signal available.
 *
 * Calibrated profiles are ground truth — those people demonstrably exist. An
 * explicit head count is the next best thing: it is the one fact the person in
 * the room knows for certain and the audio cannot supply (see ADR 0004). Typed
 * names are a weaker hint, though someone may stay silent or a visitor may
 * join. A single name or a count below two tells us nothing about the size of
 * the group, so it is ignored. With nothing to go on, fall back to thresholding
 * and tell the user the result is approximate.
 */
export function resolveSpeakerCount(opts: {
  names?: readonly string[];
  calibratedProfiles?: number;
  speakerCount?: number;
}): SpeakerCountHint {
  if (opts.calibratedProfiles !== undefined && opts.calibratedProfiles >= 2) {
    return { k: opts.calibratedProfiles, source: 'calibration', confident: true };
  }
  if (opts.speakerCount !== undefined && Number.isInteger(opts.speakerCount) && opts.speakerCount >= 2) {
    return { k: opts.speakerCount, source: 'count', confident: true };
  }
  const named = (opts.names ?? []).map((n) => n.trim()).filter(Boolean);
  if (named.length >= 2) return { k: named.length, source: 'names', confident: true };
  return { k: null, source: 'automatic', confident: false };
}
